/**
 * PubMed Geography Configuration
 *
 * Purpose
 * -------
 * Provides a reusable geography ontology and matching configuration for PubMed
 * enrichment.
 *
 * The configuration supports:
 *
 * 1. Explicit geography matching from article titles and abstracts.
 * 2. Country inference from national claims, EHR, registry, survey, and cohort names.
 * 3. Disease- and therapy-area-specific registry matching.
 * 4. Hierarchical rollups for regions and UK constituent countries.
 *
 * Design decisions
 * ----------------
 * - Regions are roll-up metadata only. They are not currently matched from article text.
 * - England, Scotland, Wales, and Northern Ireland remain distinct geography entities
 *   and roll up to the United Kingdom.
 * - A source with `inference.mode === "direct"` may automatically assign its configured
 *   geography IDs.
 * - A multinational source uses `contextual` or `none`, preventing the matcher from
 *   assigning every participating country without additional article-level evidence.

 * Recommended processing order
 * ----------------------------
 * 1. Match explicit geography aliases in the title.
 * 2. Match national data sources and disease registries in the title and abstract.
 * 3. Match explicit geography aliases in the abstract.
 * 4. Normalize and deduplicate by geography ID.
 * 5. Add parent and region rollups for filtering without replacing the specific tag.
 */

export type PubMedGeographyTextField = 'title' | 'abstract'

export type PubMedGeographyConfidence = 'high' | 'medium' | 'low'

export type GeographyRegionLevel = 'macroRegion' | 'subRegion'

export type GeographyEntityLevel = 'country' | 'constituentCountry' | 'territory' | 'specialAdministrativeRegion'

export type GeographySourceScope = 'global' | 'therapyArea'

export type GeographySourceType =
    | 'claimsDatabase'
    | 'electronicHealthRecord'
    | 'administrativeDatabase'
    | 'clinicalRegistry'
    | 'qualityRegistry'
    | 'diseaseRegistry'
    | 'cohort'
    | 'survey'
    | 'healthSystem'
    | 'auditProgram'
    | 'researchNetwork'

export type GeographyInferenceMode = 'direct' | 'contextual' | 'none'

export type GeographyTherapyAreaId = 'rheumatology' | 'metabolic' | 'dermatology' | 'respiratory'

export type GeographyIndicationId =
    | 'rheumatoid_arthritis'
    | 'obesity'
    | 'weight_management'
    | 'atopic_dermatitis'
    | 'copd'
    | 'asthma'
    | 'severe_asthma'

export interface GeographyRegionConfig {
    id: string
    label: string
    level: GeographyRegionLevel
    parentRegionId?: string
    description?: string
}

export interface GeographyEntityConfig {
    id: string
    label: string
    level: GeographyEntityLevel
    iso2?: string
    iso3?: string

    /**
     * Used for constituent countries and territories.
     * Example: Scotland -> United Kingdom.
     */
    parentGeographyId?: string

    /**
     * Region IDs are filter rollups only and are not matched directly.
     * Multiple values are allowed for transregional markets such as Türkiye.
     */
    regionIds: string[]

    /**
     * Case-insensitive literal aliases.
     * Do not place short ambiguous abbreviations here.
     */
    aliases: string[]

    /**
     * Case-sensitive literal aliases, typically acronyms such as US, UK, or UAE.
     */
    caseSensitiveAliases?: string[]

    /**
     * Demonyms are only intended for contextual matching, such as
     * "German patients" or "Japanese claims data". They should not be matched alone.
     */
    contextualDemonyms?: string[]

    /**
     * Optional custom patterns for forms that cannot be represented safely as literals.
     */
    patterns?: RegExp[]

    enabled?: boolean
}

export interface GeographySourceInferenceConfig {
    mode: GeographyInferenceMode
    geographyIds: string[]
    confidence: PubMedGeographyConfidence
    rationale: string
}

export interface GeographySourceConfig {
    id: string
    label: string
    scope: GeographySourceScope
    sourceType: GeographySourceType

    aliases: string[]
    caseSensitiveAliases?: string[]
    patterns?: RegExp[]

    inference: GeographySourceInferenceConfig

    therapyAreaIds?: GeographyTherapyAreaId[]
    indicationIds?: GeographyIndicationId[]

    /**
     * Optional source-specific notes for maintainers.
     */
    notes?: string

    enabled?: boolean
}

export interface PubMedGeographyMatch {
    geographyId: string
    geographyLabel: string
    geographyLevel: GeographyEntityLevel
    matchedField: PubMedGeographyTextField
    matchedTerm: string
    matchType: 'explicitGeography' | 'dataSource'
    matchedSourceId?: string
    matchedSourceLabel?: string
    confidence: PubMedGeographyConfidence
}

export interface PubMedGeographyEnrichment {
    studyGeographyIds: string[]
    studyGeographyLabels: string[]
    geographyMatches: PubMedGeographyMatch[]
    matchedSourceIds: string[]
    matchedSourceLabels: string[]

    /**
     * Parent countries and regions used for filters and aggregation.
     * These do not replace the specific study geography.
     */
    geographyRollupIds: string[]
    regionRollupIds: string[]
}

export const PUBMED_GEOGRAPHY_FIELD_CONFIDENCE: Record<
    string,
    Record<PubMedGeographyTextField, PubMedGeographyConfidence>
> = {
    explicitGeography: {
        title: 'high',
        abstract: 'medium',
    },
    directDataSource: {
        title: 'high',
        abstract: 'high',
    },
    contextualDataSource: {
        title: 'medium',
        abstract: 'medium',
    },
}

/**
 * Context terms used when matching demonyms.
 *
 * Example:
 *   "German patients" -> Germany
 *   "Japanese claims database" -> Japan
 *
 * Avoid matching a demonym by itself because phrases such as
 * "American College of Rheumatology criteria" do not establish study geography.
 */
export const PUBMED_GEOGRAPHY_CONTEXT_TERMS = [
    'patients?',
    'participants?',
    'population',
    'cohort',
    'registry',
    'register',
    'database',
    'claims?',
    'data',
    'cent(?:er|re)s?',
    'hospitals?',
    'sites?',
    'practices?',
    'health system',
    'healthcare system',
    'national sample',
] as const

export const PUBMED_GEOGRAPHY_REGIONS: GeographyRegionConfig[] = [
    {
        id: 'europe',
        label: 'Europe',
        level: 'macroRegion',
        description: 'Commercial and evidence rollup for European markets.',
    },
    {
        id: 'northern_europe',
        label: 'Northern Europe',
        level: 'subRegion',
        parentRegionId: 'europe',
    },
    {
        id: 'western_europe',
        label: 'Western Europe',
        level: 'subRegion',
        parentRegionId: 'europe',
    },
    {
        id: 'southern_europe',
        label: 'Southern Europe',
        level: 'subRegion',
        parentRegionId: 'europe',
    },
    {
        id: 'central_eastern_europe',
        label: 'Central and Eastern Europe',
        level: 'subRegion',
        parentRegionId: 'europe',
    },
    {
        id: 'north_america',
        label: 'North America',
        level: 'macroRegion',
    },
    {
        id: 'latin_america',
        label: 'Latin America',
        level: 'macroRegion',
    },
    {
        id: 'asia_pacific',
        label: 'Asia-Pacific',
        level: 'macroRegion',
    },
    {
        id: 'east_asia',
        label: 'East Asia',
        level: 'subRegion',
        parentRegionId: 'asia_pacific',
    },
    {
        id: 'south_asia',
        label: 'South Asia',
        level: 'subRegion',
        parentRegionId: 'asia_pacific',
    },
    {
        id: 'southeast_asia',
        label: 'Southeast Asia',
        level: 'subRegion',
        parentRegionId: 'asia_pacific',
    },
    {
        id: 'oceania',
        label: 'Oceania',
        level: 'subRegion',
        parentRegionId: 'asia_pacific',
    },
    {
        id: 'middle_east',
        label: 'Middle East',
        level: 'macroRegion',
    },
    {
        id: 'africa',
        label: 'Africa',
        level: 'macroRegion',
    },
    {
        id: 'north_africa',
        label: 'North Africa',
        level: 'subRegion',
        parentRegionId: 'africa',
    },
    {
        id: 'sub_saharan_africa',
        label: 'Sub-Saharan Africa',
        level: 'subRegion',
        parentRegionId: 'africa',
    },
]

const geography = (config: GeographyEntityConfig): GeographyEntityConfig => ({
    enabled: true,
    ...config,
})

/**
 * Canonical geography entities.
 *
 * Notes:
 * - The list emphasizes markets commonly encountered in pharmaceutical evidence.
 * - Microstates are omitted in the first release unless they are likely to appear
 *   independently in PubMed evidence.
 */
export const PUBMED_GEOGRAPHY_ENTITIES: GeographyEntityConfig[] = [
    // =========================================================
    // North America
    // =========================================================
    geography({
        id: 'united_states',
        label: 'United States',
        level: 'country',
        iso2: 'US',
        iso3: 'USA',
        regionIds: ['north_america'],
        aliases: ['United States', 'United States of America'],
        caseSensitiveAliases: ['US', 'USA', 'U.S.', 'U.S.A.'],
        contextualDemonyms: ['American', 'US-based', 'U.S.-based'],
    }),
    geography({
        id: 'canada',
        label: 'Canada',
        level: 'country',
        iso2: 'CA',
        iso3: 'CAN',
        regionIds: ['north_america'],
        aliases: ['Canada'],
        contextualDemonyms: ['Canadian'],
    }),
    geography({
        id: 'mexico',
        label: 'Mexico',
        level: 'country',
        iso2: 'MX',
        iso3: 'MEX',
        regionIds: ['north_america', 'latin_america'],
        aliases: ['Mexico', 'México'],
        contextualDemonyms: ['Mexican'],
    }),
    geography({
        id: 'puerto_rico',
        label: 'Puerto Rico',
        level: 'territory',
        iso2: 'PR',
        iso3: 'PRI',
        parentGeographyId: 'united_states',
        regionIds: ['north_america', 'latin_america'],
        aliases: ['Puerto Rico'],
        contextualDemonyms: ['Puerto Rican'],
    }),
    geography({
        id: 'costa_rica',
        label: 'Costa Rica',
        level: 'country',
        iso2: 'CR',
        iso3: 'CRI',
        regionIds: ['latin_america'],
        aliases: ['Costa Rica'],
        contextualDemonyms: ['Costa Rican'],
    }),
    geography({
        id: 'panama',
        label: 'Panama',
        level: 'country',
        iso2: 'PA',
        iso3: 'PAN',
        regionIds: ['latin_america'],
        aliases: ['Panama', 'Panamá'],
        contextualDemonyms: ['Panamanian'],
    }),

    // =========================================================
    // Latin America
    // =========================================================
    geography({
        id: 'brazil',
        label: 'Brazil',
        level: 'country',
        iso2: 'BR',
        iso3: 'BRA',
        regionIds: ['latin_america'],
        aliases: ['Brazil', 'Brasil'],
        contextualDemonyms: ['Brazilian'],
    }),
    geography({
        id: 'argentina',
        label: 'Argentina',
        level: 'country',
        iso2: 'AR',
        iso3: 'ARG',
        regionIds: ['latin_america'],
        aliases: ['Argentina'],
        contextualDemonyms: ['Argentine', 'Argentinian'],
    }),
    geography({
        id: 'chile',
        label: 'Chile',
        level: 'country',
        iso2: 'CL',
        iso3: 'CHL',
        regionIds: ['latin_america'],
        aliases: ['Chile'],
        contextualDemonyms: ['Chilean'],
    }),
    geography({
        id: 'colombia',
        label: 'Colombia',
        level: 'country',
        iso2: 'CO',
        iso3: 'COL',
        regionIds: ['latin_america'],
        aliases: ['Colombia'],
        contextualDemonyms: ['Colombian'],
    }),
    geography({
        id: 'peru',
        label: 'Peru',
        level: 'country',
        iso2: 'PE',
        iso3: 'PER',
        regionIds: ['latin_america'],
        aliases: ['Peru', 'Perú'],
        contextualDemonyms: ['Peruvian'],
    }),
    geography({
        id: 'ecuador',
        label: 'Ecuador',
        level: 'country',
        iso2: 'EC',
        iso3: 'ECU',
        regionIds: ['latin_america'],
        aliases: ['Ecuador'],
        contextualDemonyms: ['Ecuadorian'],
    }),
    geography({
        id: 'uruguay',
        label: 'Uruguay',
        level: 'country',
        iso2: 'UY',
        iso3: 'URY',
        regionIds: ['latin_america'],
        aliases: ['Uruguay'],
        contextualDemonyms: ['Uruguayan'],
    }),
    geography({
        id: 'paraguay',
        label: 'Paraguay',
        level: 'country',
        iso2: 'PY',
        iso3: 'PRY',
        regionIds: ['latin_america'],
        aliases: ['Paraguay'],
        contextualDemonyms: ['Paraguayan'],
    }),
    geography({
        id: 'bolivia',
        label: 'Bolivia',
        level: 'country',
        iso2: 'BO',
        iso3: 'BOL',
        regionIds: ['latin_america'],
        aliases: ['Bolivia', 'Plurinational State of Bolivia'],
        contextualDemonyms: ['Bolivian'],
    }),
    geography({
        id: 'venezuela',
        label: 'Venezuela',
        level: 'country',
        iso2: 'VE',
        iso3: 'VEN',
        regionIds: ['latin_america'],
        aliases: ['Venezuela'],
        contextualDemonyms: ['Venezuelan'],
    }),

    // =========================================================
    // United Kingdom and Ireland
    // =========================================================
    geography({
        id: 'united_kingdom',
        label: 'United Kingdom',
        level: 'country',
        iso2: 'GB',
        iso3: 'GBR',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['United Kingdom', 'Great Britain'],
        caseSensitiveAliases: ['UK', 'U.K.'],
        contextualDemonyms: ['British', 'UK-based', 'U.K.-based'],
    }),
    geography({
        id: 'england',
        label: 'England',
        level: 'constituentCountry',
        parentGeographyId: 'united_kingdom',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['England'],
        contextualDemonyms: ['English'],
    }),
    geography({
        id: 'scotland',
        label: 'Scotland',
        level: 'constituentCountry',
        parentGeographyId: 'united_kingdom',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['Scotland'],
        contextualDemonyms: ['Scottish'],
    }),
    geography({
        id: 'wales',
        label: 'Wales',
        level: 'constituentCountry',
        parentGeographyId: 'united_kingdom',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['Wales'],
        contextualDemonyms: ['Welsh'],
    }),
    geography({
        id: 'northern_ireland',
        label: 'Northern Ireland',
        level: 'constituentCountry',
        parentGeographyId: 'united_kingdom',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['Northern Ireland'],
        contextualDemonyms: ['Northern Irish'],
    }),
    geography({
        id: 'ireland',
        label: 'Ireland',
        level: 'country',
        iso2: 'IE',
        iso3: 'IRL',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['Ireland', 'Republic of Ireland'],
        contextualDemonyms: ['Irish'],
    }),

    // =========================================================
    // Western and Northern Europe
    // =========================================================
    geography({
        id: 'france',
        label: 'France',
        level: 'country',
        iso2: 'FR',
        iso3: 'FRA',
        regionIds: ['europe', 'western_europe'],
        aliases: ['France'],
        contextualDemonyms: ['French'],
    }),
    geography({
        id: 'germany',
        label: 'Germany',
        level: 'country',
        iso2: 'DE',
        iso3: 'DEU',
        regionIds: ['europe', 'western_europe'],
        aliases: ['Germany'],
        contextualDemonyms: ['German'],
    }),
    geography({
        id: 'netherlands',
        label: 'Netherlands',
        level: 'country',
        iso2: 'NL',
        iso3: 'NLD',
        regionIds: ['europe', 'western_europe'],
        aliases: ['Netherlands', 'The Netherlands'],
        contextualDemonyms: ['Dutch'],
    }),
    geography({
        id: 'belgium',
        label: 'Belgium',
        level: 'country',
        iso2: 'BE',
        iso3: 'BEL',
        regionIds: ['europe', 'western_europe'],
        aliases: ['Belgium'],
        contextualDemonyms: ['Belgian'],
    }),
    geography({
        id: 'luxembourg',
        label: 'Luxembourg',
        level: 'country',
        iso2: 'LU',
        iso3: 'LUX',
        regionIds: ['europe', 'western_europe'],
        aliases: ['Luxembourg'],
        contextualDemonyms: ['Luxembourgish'],
    }),
    geography({
        id: 'switzerland',
        label: 'Switzerland',
        level: 'country',
        iso2: 'CH',
        iso3: 'CHE',
        regionIds: ['europe', 'western_europe'],
        aliases: ['Switzerland'],
        contextualDemonyms: ['Swiss'],
    }),
    geography({
        id: 'austria',
        label: 'Austria',
        level: 'country',
        iso2: 'AT',
        iso3: 'AUT',
        regionIds: ['europe', 'western_europe'],
        aliases: ['Austria'],
        contextualDemonyms: ['Austrian'],
    }),
    geography({
        id: 'denmark',
        label: 'Denmark',
        level: 'country',
        iso2: 'DK',
        iso3: 'DNK',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['Denmark'],
        contextualDemonyms: ['Danish'],
    }),
    geography({
        id: 'sweden',
        label: 'Sweden',
        level: 'country',
        iso2: 'SE',
        iso3: 'SWE',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['Sweden'],
        contextualDemonyms: ['Swedish'],
    }),
    geography({
        id: 'norway',
        label: 'Norway',
        level: 'country',
        iso2: 'NO',
        iso3: 'NOR',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['Norway'],
        contextualDemonyms: ['Norwegian'],
    }),
    geography({
        id: 'finland',
        label: 'Finland',
        level: 'country',
        iso2: 'FI',
        iso3: 'FIN',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['Finland'],
        contextualDemonyms: ['Finnish'],
    }),
    geography({
        id: 'iceland',
        label: 'Iceland',
        level: 'country',
        iso2: 'IS',
        iso3: 'ISL',
        regionIds: ['europe', 'northern_europe'],
        aliases: ['Iceland'],
        contextualDemonyms: ['Icelandic'],
    }),

    // =========================================================
    // Southern Europe
    // =========================================================
    geography({
        id: 'italy',
        label: 'Italy',
        level: 'country',
        iso2: 'IT',
        iso3: 'ITA',
        regionIds: ['europe', 'southern_europe'],
        aliases: ['Italy'],
        contextualDemonyms: ['Italian'],
    }),
    geography({
        id: 'spain',
        label: 'Spain',
        level: 'country',
        iso2: 'ES',
        iso3: 'ESP',
        regionIds: ['europe', 'southern_europe'],
        aliases: ['Spain'],
        contextualDemonyms: ['Spanish'],
    }),
    geography({
        id: 'portugal',
        label: 'Portugal',
        level: 'country',
        iso2: 'PT',
        iso3: 'PRT',
        regionIds: ['europe', 'southern_europe'],
        aliases: ['Portugal'],
        contextualDemonyms: ['Portuguese'],
    }),
    geography({
        id: 'greece',
        label: 'Greece',
        level: 'country',
        iso2: 'GR',
        iso3: 'GRC',
        regionIds: ['europe', 'southern_europe'],
        aliases: ['Greece'],
        contextualDemonyms: ['Greek'],
    }),
    geography({
        id: 'cyprus',
        label: 'Cyprus',
        level: 'country',
        iso2: 'CY',
        iso3: 'CYP',
        regionIds: ['europe', 'southern_europe', 'middle_east'],
        aliases: ['Cyprus'],
        contextualDemonyms: ['Cypriot'],
    }),
    geography({
        id: 'malta',
        label: 'Malta',
        level: 'country',
        iso2: 'MT',
        iso3: 'MLT',
        regionIds: ['europe', 'southern_europe'],
        aliases: ['Malta'],
        contextualDemonyms: ['Maltese'],
    }),

    // =========================================================
    // Central and Eastern Europe
    // =========================================================
    geography({
        id: 'poland',
        label: 'Poland',
        level: 'country',
        iso2: 'PL',
        iso3: 'POL',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Poland'],
        contextualDemonyms: ['Polish'],
    }),
    geography({
        id: 'czechia',
        label: 'Czechia',
        level: 'country',
        iso2: 'CZ',
        iso3: 'CZE',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Czechia', 'Czech Republic'],
        contextualDemonyms: ['Czech'],
    }),
    geography({
        id: 'slovakia',
        label: 'Slovakia',
        level: 'country',
        iso2: 'SK',
        iso3: 'SVK',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Slovakia', 'Slovak Republic'],
        contextualDemonyms: ['Slovak'],
    }),
    geography({
        id: 'hungary',
        label: 'Hungary',
        level: 'country',
        iso2: 'HU',
        iso3: 'HUN',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Hungary'],
        contextualDemonyms: ['Hungarian'],
    }),
    geography({
        id: 'romania',
        label: 'Romania',
        level: 'country',
        iso2: 'RO',
        iso3: 'ROU',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Romania'],
        contextualDemonyms: ['Romanian'],
    }),
    geography({
        id: 'bulgaria',
        label: 'Bulgaria',
        level: 'country',
        iso2: 'BG',
        iso3: 'BGR',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Bulgaria'],
        contextualDemonyms: ['Bulgarian'],
    }),
    geography({
        id: 'slovenia',
        label: 'Slovenia',
        level: 'country',
        iso2: 'SI',
        iso3: 'SVN',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Slovenia'],
        contextualDemonyms: ['Slovenian'],
    }),
    geography({
        id: 'croatia',
        label: 'Croatia',
        level: 'country',
        iso2: 'HR',
        iso3: 'HRV',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Croatia'],
        contextualDemonyms: ['Croatian'],
    }),
    geography({
        id: 'serbia',
        label: 'Serbia',
        level: 'country',
        iso2: 'RS',
        iso3: 'SRB',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Serbia'],
        contextualDemonyms: ['Serbian'],
    }),
    geography({
        id: 'bosnia_and_herzegovina',
        label: 'Bosnia and Herzegovina',
        level: 'country',
        iso2: 'BA',
        iso3: 'BIH',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Bosnia and Herzegovina', 'Bosnia-Herzegovina'],
        contextualDemonyms: ['Bosnian'],
    }),
    geography({
        id: 'montenegro',
        label: 'Montenegro',
        level: 'country',
        iso2: 'ME',
        iso3: 'MNE',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Montenegro'],
        contextualDemonyms: ['Montenegrin'],
    }),
    geography({
        id: 'north_macedonia',
        label: 'North Macedonia',
        level: 'country',
        iso2: 'MK',
        iso3: 'MKD',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['North Macedonia', 'Republic of North Macedonia'],
        contextualDemonyms: ['Macedonian'],
    }),
    geography({
        id: 'albania',
        label: 'Albania',
        level: 'country',
        iso2: 'AL',
        iso3: 'ALB',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Albania'],
        contextualDemonyms: ['Albanian'],
    }),
    geography({
        id: 'estonia',
        label: 'Estonia',
        level: 'country',
        iso2: 'EE',
        iso3: 'EST',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Estonia'],
        contextualDemonyms: ['Estonian'],
    }),
    geography({
        id: 'latvia',
        label: 'Latvia',
        level: 'country',
        iso2: 'LV',
        iso3: 'LVA',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Latvia'],
        contextualDemonyms: ['Latvian'],
    }),
    geography({
        id: 'lithuania',
        label: 'Lithuania',
        level: 'country',
        iso2: 'LT',
        iso3: 'LTU',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Lithuania'],
        contextualDemonyms: ['Lithuanian'],
    }),
    geography({
        id: 'ukraine',
        label: 'Ukraine',
        level: 'country',
        iso2: 'UA',
        iso3: 'UKR',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Ukraine'],
        contextualDemonyms: ['Ukrainian'],
    }),
    geography({
        id: 'russia',
        label: 'Russia',
        level: 'country',
        iso2: 'RU',
        iso3: 'RUS',
        regionIds: ['europe', 'central_eastern_europe', 'asia_pacific'],
        aliases: ['Russia', 'Russian Federation'],
        contextualDemonyms: ['Russian'],
    }),
    geography({
        id: 'belarus',
        label: 'Belarus',
        level: 'country',
        iso2: 'BY',
        iso3: 'BLR',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Belarus'],
        contextualDemonyms: ['Belarusian'],
    }),
    geography({
        id: 'moldova',
        label: 'Moldova',
        level: 'country',
        iso2: 'MD',
        iso3: 'MDA',
        regionIds: ['europe', 'central_eastern_europe'],
        aliases: ['Moldova', 'Republic of Moldova'],
        contextualDemonyms: ['Moldovan'],
    }),
    geography({
        id: 'turkiye',
        label: 'Türkiye',
        level: 'country',
        iso2: 'TR',
        iso3: 'TUR',
        regionIds: ['europe', 'southern_europe', 'middle_east'],
        aliases: ['Türkiye', 'Turkey', 'Republic of Türkiye', 'Republic of Turkey'],
        contextualDemonyms: ['Turkish'],
    }),

    // =========================================================
    // East Asia
    // =========================================================
    geography({
        id: 'china',
        label: 'China',
        level: 'country',
        iso2: 'CN',
        iso3: 'CHN',
        regionIds: ['asia_pacific', 'east_asia'],
        aliases: ['China', "People's Republic of China", 'PR China'],
        caseSensitiveAliases: ['PRC'],
        contextualDemonyms: ['Chinese'],
    }),
    geography({
        id: 'taiwan',
        label: 'Taiwan',
        level: 'country',
        iso2: 'TW',
        iso3: 'TWN',
        regionIds: ['asia_pacific', 'east_asia'],
        aliases: ['Taiwan'],
        contextualDemonyms: ['Taiwanese'],
    }),
    geography({
        id: 'hong_kong',
        label: 'Hong Kong',
        level: 'specialAdministrativeRegion',
        iso2: 'HK',
        iso3: 'HKG',
        regionIds: ['asia_pacific', 'east_asia'],
        aliases: ['Hong Kong'],
        caseSensitiveAliases: ['HKSAR'],
        contextualDemonyms: ['Hong Kong'],
    }),
    geography({
        id: 'japan',
        label: 'Japan',
        level: 'country',
        iso2: 'JP',
        iso3: 'JPN',
        regionIds: ['asia_pacific', 'east_asia'],
        aliases: ['Japan'],
        contextualDemonyms: ['Japanese'],
    }),
    geography({
        id: 'south_korea',
        label: 'South Korea',
        level: 'country',
        iso2: 'KR',
        iso3: 'KOR',
        regionIds: ['asia_pacific', 'east_asia'],
        aliases: ['South Korea', 'Republic of Korea'],
        contextualDemonyms: ['South Korean', 'Korean'],
    }),

    // =========================================================
    // South and Southeast Asia
    // =========================================================
    geography({
        id: 'india',
        label: 'India',
        level: 'country',
        iso2: 'IN',
        iso3: 'IND',
        regionIds: ['asia_pacific', 'south_asia'],
        aliases: ['India'],
        contextualDemonyms: ['Indian'],
    }),
    geography({
        id: 'pakistan',
        label: 'Pakistan',
        level: 'country',
        iso2: 'PK',
        iso3: 'PAK',
        regionIds: ['asia_pacific', 'south_asia'],
        aliases: ['Pakistan'],
        contextualDemonyms: ['Pakistani'],
    }),
    geography({
        id: 'bangladesh',
        label: 'Bangladesh',
        level: 'country',
        iso2: 'BD',
        iso3: 'BGD',
        regionIds: ['asia_pacific', 'south_asia'],
        aliases: ['Bangladesh'],
        contextualDemonyms: ['Bangladeshi'],
    }),
    geography({
        id: 'sri_lanka',
        label: 'Sri Lanka',
        level: 'country',
        iso2: 'LK',
        iso3: 'LKA',
        regionIds: ['asia_pacific', 'south_asia'],
        aliases: ['Sri Lanka'],
        contextualDemonyms: ['Sri Lankan'],
    }),
    geography({
        id: 'singapore',
        label: 'Singapore',
        level: 'country',
        iso2: 'SG',
        iso3: 'SGP',
        regionIds: ['asia_pacific', 'southeast_asia'],
        aliases: ['Singapore'],
        contextualDemonyms: ['Singaporean'],
    }),
    geography({
        id: 'malaysia',
        label: 'Malaysia',
        level: 'country',
        iso2: 'MY',
        iso3: 'MYS',
        regionIds: ['asia_pacific', 'southeast_asia'],
        aliases: ['Malaysia'],
        contextualDemonyms: ['Malaysian'],
    }),
    geography({
        id: 'thailand',
        label: 'Thailand',
        level: 'country',
        iso2: 'TH',
        iso3: 'THA',
        regionIds: ['asia_pacific', 'southeast_asia'],
        aliases: ['Thailand'],
        contextualDemonyms: ['Thai'],
    }),
    geography({
        id: 'indonesia',
        label: 'Indonesia',
        level: 'country',
        iso2: 'ID',
        iso3: 'IDN',
        regionIds: ['asia_pacific', 'southeast_asia'],
        aliases: ['Indonesia'],
        contextualDemonyms: ['Indonesian'],
    }),
    geography({
        id: 'philippines',
        label: 'Philippines',
        level: 'country',
        iso2: 'PH',
        iso3: 'PHL',
        regionIds: ['asia_pacific', 'southeast_asia'],
        aliases: ['Philippines', 'The Philippines'],
        contextualDemonyms: ['Filipino', 'Philippine'],
    }),
    geography({
        id: 'vietnam',
        label: 'Vietnam',
        level: 'country',
        iso2: 'VN',
        iso3: 'VNM',
        regionIds: ['asia_pacific', 'southeast_asia'],
        aliases: ['Vietnam', 'Viet Nam'],
        contextualDemonyms: ['Vietnamese'],
    }),

    // =========================================================
    // Oceania
    // =========================================================
    geography({
        id: 'australia',
        label: 'Australia',
        level: 'country',
        iso2: 'AU',
        iso3: 'AUS',
        regionIds: ['asia_pacific', 'oceania'],
        aliases: ['Australia'],
        contextualDemonyms: ['Australian'],
    }),
    geography({
        id: 'new_zealand',
        label: 'New Zealand',
        level: 'country',
        iso2: 'NZ',
        iso3: 'NZL',
        regionIds: ['asia_pacific', 'oceania'],
        aliases: ['New Zealand'],
        contextualDemonyms: ['New Zealand'],
    }),

    // =========================================================
    // Middle East
    // =========================================================
    geography({
        id: 'israel',
        label: 'Israel',
        level: 'country',
        iso2: 'IL',
        iso3: 'ISR',
        regionIds: ['middle_east'],
        aliases: ['Israel'],
        contextualDemonyms: ['Israeli'],
    }),
    geography({
        id: 'saudi_arabia',
        label: 'Saudi Arabia',
        level: 'country',
        iso2: 'SA',
        iso3: 'SAU',
        regionIds: ['middle_east'],
        aliases: ['Saudi Arabia', 'Kingdom of Saudi Arabia'],
        caseSensitiveAliases: ['KSA'],
        contextualDemonyms: ['Saudi'],
    }),
    geography({
        id: 'united_arab_emirates',
        label: 'United Arab Emirates',
        level: 'country',
        iso2: 'AE',
        iso3: 'ARE',
        regionIds: ['middle_east'],
        aliases: ['United Arab Emirates'],
        caseSensitiveAliases: ['UAE', 'U.A.E.'],
        contextualDemonyms: ['Emirati'],
    }),
    geography({
        id: 'qatar',
        label: 'Qatar',
        level: 'country',
        iso2: 'QA',
        iso3: 'QAT',
        regionIds: ['middle_east'],
        aliases: ['Qatar'],
        contextualDemonyms: ['Qatari'],
    }),
    geography({
        id: 'kuwait',
        label: 'Kuwait',
        level: 'country',
        iso2: 'KW',
        iso3: 'KWT',
        regionIds: ['middle_east'],
        aliases: ['Kuwait'],
        contextualDemonyms: ['Kuwaiti'],
    }),
    geography({
        id: 'bahrain',
        label: 'Bahrain',
        level: 'country',
        iso2: 'BH',
        iso3: 'BHR',
        regionIds: ['middle_east'],
        aliases: ['Bahrain'],
        contextualDemonyms: ['Bahraini'],
    }),
    geography({
        id: 'oman',
        label: 'Oman',
        level: 'country',
        iso2: 'OM',
        iso3: 'OMN',
        regionIds: ['middle_east'],
        aliases: ['Oman'],
        contextualDemonyms: ['Omani'],
    }),
    geography({
        id: 'jordan',
        label: 'Jordan',
        level: 'country',
        iso2: 'JO',
        iso3: 'JOR',
        regionIds: ['middle_east'],
        aliases: ['Jordan'],
        contextualDemonyms: ['Jordanian'],
    }),
    geography({
        id: 'lebanon',
        label: 'Lebanon',
        level: 'country',
        iso2: 'LB',
        iso3: 'LBN',
        regionIds: ['middle_east'],
        aliases: ['Lebanon'],
        contextualDemonyms: ['Lebanese'],
    }),
    geography({
        id: 'iran',
        label: 'Iran',
        level: 'country',
        iso2: 'IR',
        iso3: 'IRN',
        regionIds: ['middle_east'],
        aliases: ['Iran', 'Islamic Republic of Iran'],
        contextualDemonyms: ['Iranian'],
    }),
    geography({
        id: 'iraq',
        label: 'Iraq',
        level: 'country',
        iso2: 'IQ',
        iso3: 'IRQ',
        regionIds: ['middle_east'],
        aliases: ['Iraq'],
        contextualDemonyms: ['Iraqi'],
    }),

    // =========================================================
    // Africa
    // =========================================================
    geography({
        id: 'south_africa',
        label: 'South Africa',
        level: 'country',
        iso2: 'ZA',
        iso3: 'ZAF',
        regionIds: ['africa', 'sub_saharan_africa'],
        aliases: ['South Africa', 'Republic of South Africa'],
        contextualDemonyms: ['South African'],
    }),
    geography({
        id: 'egypt',
        label: 'Egypt',
        level: 'country',
        iso2: 'EG',
        iso3: 'EGY',
        regionIds: ['africa', 'north_africa', 'middle_east'],
        aliases: ['Egypt'],
        contextualDemonyms: ['Egyptian'],
    }),
    geography({
        id: 'morocco',
        label: 'Morocco',
        level: 'country',
        iso2: 'MA',
        iso3: 'MAR',
        regionIds: ['africa', 'north_africa'],
        aliases: ['Morocco'],
        contextualDemonyms: ['Moroccan'],
    }),
    geography({
        id: 'algeria',
        label: 'Algeria',
        level: 'country',
        iso2: 'DZ',
        iso3: 'DZA',
        regionIds: ['africa', 'north_africa'],
        aliases: ['Algeria'],
        contextualDemonyms: ['Algerian'],
    }),
    geography({
        id: 'tunisia',
        label: 'Tunisia',
        level: 'country',
        iso2: 'TN',
        iso3: 'TUN',
        regionIds: ['africa', 'north_africa'],
        aliases: ['Tunisia'],
        contextualDemonyms: ['Tunisian'],
    }),
    geography({
        id: 'nigeria',
        label: 'Nigeria',
        level: 'country',
        iso2: 'NG',
        iso3: 'NGA',
        regionIds: ['africa', 'sub_saharan_africa'],
        aliases: ['Nigeria'],
        contextualDemonyms: ['Nigerian'],
    }),
    geography({
        id: 'kenya',
        label: 'Kenya',
        level: 'country',
        iso2: 'KE',
        iso3: 'KEN',
        regionIds: ['africa', 'sub_saharan_africa'],
        aliases: ['Kenya'],
        contextualDemonyms: ['Kenyan'],
    }),
    geography({
        id: 'ghana',
        label: 'Ghana',
        level: 'country',
        iso2: 'GH',
        iso3: 'GHA',
        regionIds: ['africa', 'sub_saharan_africa'],
        aliases: ['Ghana'],
        contextualDemonyms: ['Ghanaian'],
    }),
    geography({
        id: 'ethiopia',
        label: 'Ethiopia',
        level: 'country',
        iso2: 'ET',
        iso3: 'ETH',
        regionIds: ['africa', 'sub_saharan_africa'],
        aliases: ['Ethiopia'],
        contextualDemonyms: ['Ethiopian'],
    }),
    geography({
        id: 'uganda',
        label: 'Uganda',
        level: 'country',
        iso2: 'UG',
        iso3: 'UGA',
        regionIds: ['africa', 'sub_saharan_africa'],
        aliases: ['Uganda'],
        contextualDemonyms: ['Ugandan'],
    }),
    geography({
        id: 'tanzania',
        label: 'Tanzania',
        level: 'country',
        iso2: 'TZ',
        iso3: 'TZA',
        regionIds: ['africa', 'sub_saharan_africa'],
        aliases: ['Tanzania', 'United Republic of Tanzania'],
        contextualDemonyms: ['Tanzanian'],
    }),
]

const source = (config: GeographySourceConfig): GeographySourceConfig => ({
    enabled: true,
    ...config,
})

/**
 * Global national and regional data sources.
 *
 * These are loaded for every PubMed search, regardless of indication.
 */
export const CORE_PUBMED_GEOGRAPHY_SOURCES: GeographySourceConfig[] = [
    // =========================================================
    // United States
    // =========================================================
    source({
        id: 'marketscan',
        label: 'MarketScan',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: [
            'IBM MarketScan',
            'Truven MarketScan',
            'Merative MarketScan',
            'MarketScan Commercial Claims',
            'MarketScan Medicare Supplemental',
        ],
        caseSensitiveAliases: ['MarketScan'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'MarketScan is a United States claims data source.',
        },
    }),
    source({
        id: 'optum_clinformatics',
        label: 'Optum Clinformatics Data Mart',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['Optum Clinformatics Data Mart', 'Clinformatics Data Mart', 'Optum claims database'],
        caseSensitiveAliases: ['Optum Clinformatics'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'Clinformatics is a United States administrative claims data source.',
        },
    }),
    source({
        id: 'iqvia',
        label: 'IQVIA PharMetrics Plus',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['PharMetrics', 'IMS PharMetrics', 'QuintilesIMS PharMetrics Plus'],
        caseSensitiveAliases: ['PharMetrics'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'PharmMetrics is a United States claims data source.',
        },
    }),
    source({
        id: 'medicare',
        label: 'Medicare',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: [
            'Medicare claims',
            'Medicare database',
            'Centers for Medicare and Medicaid Services',
            'Centers for Medicare & Medicaid Services',
        ],
        caseSensitiveAliases: ['CMS Medicare'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'Medicare is a United States federal insurance program.',
        },
    }),
    source({
        id: 'medicaid',
        label: 'Medicaid',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['Medicaid claims', 'Medicaid database'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'Medicaid is a United States public insurance program.',
        },
    }),
    source({
        id: 'seer',
        label: 'SEER',
        scope: 'global',
        sourceType: 'clinicalRegistry',
        aliases: ['Surveillance, Epidemiology, and End Results'],
        caseSensitiveAliases: ['SEER'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'SEER is a United States cancer registry program.',
        },
    }),
    source({
        id: 'veterans_health_administration',
        label: 'Veterans Health Administration',
        scope: 'global',
        sourceType: 'healthSystem',
        aliases: [
            'Veterans Health Administration',
            'Veterans Affairs healthcare system',
            'Veterans Affairs health care system',
            'VA healthcare system',
            'VA medical centers',
        ],
        caseSensitiveAliases: ['VHA'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'The Veterans Health Administration is a United States health system.',
        },
    }),
    source({
        id: 'nhanes',
        label: 'National Health and Nutrition Examination Survey',
        scope: 'global',
        sourceType: 'survey',
        aliases: ['National Health and Nutrition Examination Survey'],
        caseSensitiveAliases: ['NHANES'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'NHANES is a United States national survey.',
        },
    }),
    source({
        id: 'hcup',
        label: 'Healthcare Cost and Utilization Project',
        scope: 'global',
        sourceType: 'administrativeDatabase',
        aliases: ['Healthcare Cost and Utilization Project'],
        caseSensitiveAliases: ['HCUP'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'HCUP contains United States hospital and utilization datasets.',
        },
    }),
    source({
        id: 'optum_ehr',
        label: 'Optum EHR',
        scope: 'global',
        sourceType: 'electronicHealthRecord',
        aliases: [
            'Optum EHR Data',
            'Optum EHR database',
            'Optum electronic health record data',
            'Optum electronic health record database',
            'Optum de-identified EHR data',
            'Optum de-identified electronic health record data',
        ],
        caseSensitiveAliases: ['Optum EHR'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'Optum EHR data are sourced from provider organizations across the United States.',
        },
    }),
    source({
        id: 'oracle_health_ehr_rwd',
        label: 'Oracle Health Real-World Data',
        scope: 'global',
        sourceType: 'electronicHealthRecord',
        aliases: [
            'Oracle Health Real-World Data',
            'Oracle Health EHR Real-World Data',
            'Oracle EHR Real-World Data',
            'Oracle Health EHR database',
            'Cerner Real-World Data',
            'Cerner EHR Real-World Data',
            'Cerner real-world database',
        ],
        caseSensitiveAliases: ['Oracle EHR RWD', 'Cerner EHR RWD'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale:
                'Oracle Health Real-World Data are described and benchmarked as a United States EHR dataset using U.S. states and Census regions.',
        },
    }),

    // =========================================================
    // United Kingdom
    // =========================================================
    source({
        id: 'cprd',
        label: 'Clinical Practice Research Datalink',
        scope: 'global',
        sourceType: 'electronicHealthRecord',
        aliases: ['Clinical Practice Research Datalink', 'General Practice Research Database'],
        caseSensitiveAliases: ['CPRD', 'GPRD'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_kingdom'],
            confidence: 'high',
            rationale: 'CPRD/GPRD is a United Kingdom primary-care data source.',
        },
    }),
    source({
        id: 'hospital_episode_statistics',
        label: 'Hospital Episode Statistics',
        scope: 'global',
        sourceType: 'administrativeDatabase',
        aliases: ['Hospital Episode Statistics', 'HES database', 'HES data'],
        inference: {
            mode: 'direct',
            geographyIds: ['england'],
            confidence: 'high',
            rationale: 'Hospital Episode Statistics describe NHS hospital activity in England.',
        },
    }),
    source({
        id: 'uk_biobank',
        label: 'UK Biobank',
        scope: 'global',
        sourceType: 'cohort',
        aliases: ['UK Biobank'],
        caseSensitiveAliases: ['UKB'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_kingdom'],
            confidence: 'high',
            rationale: 'UK Biobank recruited participants in the United Kingdom.',
        },
    }),
    source({
        id: 'qresearch',
        label: 'QResearch',
        scope: 'global',
        sourceType: 'electronicHealthRecord',
        aliases: ['QResearch database', 'QResearch primary care database'],
        caseSensitiveAliases: ['QResearch'],
        inference: {
            mode: 'direct',
            geographyIds: ['england'],
            confidence: 'high',
            rationale: 'QResearch is derived from primary-care practices in England.',
        },
    }),

    // =========================================================
    // France
    // =========================================================
    source({
        id: 'snds',
        label: 'French National Health Data System',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['French National Health Data System', 'Système National des Données de Santé'],
        caseSensitiveAliases: ['SNDS'],
        inference: {
            mode: 'direct',
            geographyIds: ['france'],
            confidence: 'high',
            rationale: 'SNDS is the French national health data system.',
        },
    }),
    source({
        id: 'sniiram',
        label: 'SNIIRAM',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: [
            "Système National d'Information Interrégimes de l'Assurance Maladie",
            'French national health insurance database',
        ],
        caseSensitiveAliases: ['SNIIRAM'],
        inference: {
            mode: 'direct',
            geographyIds: ['france'],
            confidence: 'high',
            rationale: 'SNIIRAM is a French national health-insurance data source.',
        },
    }),

    // =========================================================
    // Germany
    // =========================================================
    source({
        id: 'ingef',
        label: 'InGef Research Database',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['InGef Research Database', 'Institute for Applied Health Research Berlin'],
        caseSensitiveAliases: ['InGef'],
        inference: {
            mode: 'direct',
            geographyIds: ['germany'],
            confidence: 'high',
            rationale: 'InGef contains German statutory health-insurance claims.',
        },
    }),
    source({
        id: 'gepard',
        label: 'German Pharmacoepidemiological Research Database',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['German Pharmacoepidemiological Research Database'],
        caseSensitiveAliases: ['GePaRD'],
        inference: {
            mode: 'direct',
            geographyIds: ['germany'],
            confidence: 'high',
            rationale: 'GePaRD is a German claims research database.',
        },
    }),

    // =========================================================
    // Spain
    // =========================================================
    source({
        id: 'bifap',
        label: 'BIFAP',
        scope: 'global',
        sourceType: 'electronicHealthRecord',
        aliases: ['Base de Datos para la Investigación Farmacoepidemiológica en Atención Primaria'],
        caseSensitiveAliases: ['BIFAP'],
        inference: {
            mode: 'direct',
            geographyIds: ['spain'],
            confidence: 'high',
            rationale: 'BIFAP is a Spanish primary-care data source.',
        },
    }),
    source({
        id: 'sidiap',
        label: 'SIDIAP',
        scope: 'global',
        sourceType: 'electronicHealthRecord',
        aliases: [
            'Information System for Research in Primary Care',
            "Sistema d'Informació per al Desenvolupament de la Investigació en Atenció Primària",
        ],
        caseSensitiveAliases: ['SIDIAP'],
        inference: {
            mode: 'direct',
            geographyIds: ['spain'],
            confidence: 'high',
            rationale: 'SIDIAP contains primary-care data from Catalonia, Spain.',
        },
    }),

    // =========================================================
    // Canada
    // =========================================================
    source({
        id: 'ices',
        label: 'ICES',
        scope: 'global',
        sourceType: 'administrativeDatabase',
        aliases: ['Institute for Clinical Evaluative Sciences'],
        caseSensitiveAliases: ['ICES'],
        inference: {
            mode: 'direct',
            geographyIds: ['canada'],
            confidence: 'high',
            rationale: 'ICES uses Canadian, primarily Ontario, administrative health data.',
        },
    }),
    source({
        id: 'cihi',
        label: 'Canadian Institute for Health Information',
        scope: 'global',
        sourceType: 'administrativeDatabase',
        aliases: ['Canadian Institute for Health Information'],
        caseSensitiveAliases: ['CIHI'],
        inference: {
            mode: 'direct',
            geographyIds: ['canada'],
            confidence: 'high',
            rationale: 'CIHI is a Canadian health-information data source.',
        },
    }),

    // =========================================================
    // Nordic countries and Netherlands
    // =========================================================
    source({
        id: 'swedish_national_patient_register',
        label: 'Swedish National Patient Register',
        scope: 'global',
        sourceType: 'administrativeDatabase',
        aliases: ['Swedish National Patient Register', 'Swedish National Patient Registry', 'Swedish Patient Register'],
        inference: {
            mode: 'direct',
            geographyIds: ['sweden'],
            confidence: 'high',
            rationale: 'The source is a Swedish national patient register.',
        },
    }),
    source({
        id: 'danish_national_patient_registry',
        label: 'Danish National Patient Registry',
        scope: 'global',
        sourceType: 'administrativeDatabase',
        aliases: ['Danish National Patient Registry', 'Danish National Patient Register', 'Danish Patient Registry'],
        caseSensitiveAliases: ['DNPR'],
        inference: {
            mode: 'direct',
            geographyIds: ['denmark'],
            confidence: 'high',
            rationale: 'The source is a Danish national patient registry.',
        },
    }),
    source({
        id: 'norwegian_patient_registry',
        label: 'Norwegian Patient Registry',
        scope: 'global',
        sourceType: 'administrativeDatabase',
        aliases: ['Norwegian Patient Registry', 'Norwegian Patient Register'],
        caseSensitiveAliases: ['NPR-NO'],
        inference: {
            mode: 'direct',
            geographyIds: ['norway'],
            confidence: 'high',
            rationale: 'The source is a Norwegian national patient registry.',
        },
    }),
    source({
        id: 'finland_hilmo',
        label: 'Care Register for Health Care',
        scope: 'global',
        sourceType: 'administrativeDatabase',
        aliases: ['Care Register for Health Care', 'Finnish Hospital Discharge Register'],
        caseSensitiveAliases: ['HILMO'],
        inference: {
            mode: 'direct',
            geographyIds: ['finland'],
            confidence: 'high',
            rationale: 'HILMO is a Finnish national care register.',
        },
    }),
    source({
        id: 'pharmo',
        label: 'PHARMO Database Network',
        scope: 'global',
        sourceType: 'electronicHealthRecord',
        aliases: ['PHARMO Database Network'],
        caseSensitiveAliases: ['PHARMO'],
        inference: {
            mode: 'direct',
            geographyIds: ['netherlands'],
            confidence: 'high',
            rationale: 'PHARMO is a Netherlands-based health-data network.',
        },
    }),

    // =========================================================
    // Asia-Pacific
    // =========================================================
    source({
        id: 'jmdc',
        label: 'JMDC Claims Database',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['JMDC Claims Database', 'JMDC database'],
        caseSensitiveAliases: ['JMDC'],
        inference: {
            mode: 'direct',
            geographyIds: ['japan'],
            confidence: 'high',
            rationale: 'JMDC is a Japanese claims database.',
        },
    }),
    source({
        id: 'medical_data_vision',
        label: 'Medical Data Vision',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['Medical Data Vision database', 'Medical Data Vision claims database'],
        caseSensitiveAliases: ['MDV database', 'MDV claims database'],
        inference: {
            mode: 'direct',
            geographyIds: ['japan'],
            confidence: 'high',
            rationale: 'Medical Data Vision is a Japanese hospital claims database.',
        },
    }),
    source({
        id: 'japan_ndb',
        label: 'National Database of Health Insurance Claims and Specific Health Checkups of Japan',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: [
            'National Database of Health Insurance Claims and Specific Health Checkups of Japan',
            'Japanese National Database of Health Insurance Claims',
        ],
        caseSensitiveAliases: ['NDB Japan'],
        inference: {
            mode: 'direct',
            geographyIds: ['japan'],
            confidence: 'high',
            rationale: 'This is a Japanese national claims database.',
        },
    }),
    source({
        id: 'taiwan_nhird',
        label: 'Taiwan National Health Insurance Research Database',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: [
            'National Health Insurance Research Database',
            'Taiwan National Health Insurance Database',
            'Taiwanese National Health Insurance Research Database',
        ],
        caseSensitiveAliases: ['NHIRD'],
        inference: {
            mode: 'direct',
            geographyIds: ['taiwan'],
            confidence: 'high',
            rationale: 'NHIRD is a Taiwanese national health-insurance database.',
        },
    }),
    source({
        id: 'korea_hira',
        label: 'Health Insurance Review and Assessment Service',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['Health Insurance Review and Assessment Service'],
        caseSensitiveAliases: ['HIRA'],
        inference: {
            mode: 'direct',
            geographyIds: ['south_korea'],
            confidence: 'high',
            rationale: 'HIRA is a South Korean national health-insurance data source.',
        },
    }),
    source({
        id: 'korea_nhis',
        label: 'Korean National Health Insurance Service',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['Korean National Health Insurance Service', 'Korea National Health Insurance Service'],
        caseSensitiveAliases: ['K-NHIS', 'NHIS Korea'],
        inference: {
            mode: 'direct',
            geographyIds: ['south_korea'],
            confidence: 'high',
            rationale: 'This is a South Korean national health-insurance data source.',
        },
    }),
    source({
        id: 'hong_kong_cdars',
        label: 'Clinical Data Analysis and Reporting System',
        scope: 'global',
        sourceType: 'electronicHealthRecord',
        aliases: ['Clinical Data Analysis and Reporting System'],
        caseSensitiveAliases: ['CDARS'],
        inference: {
            mode: 'direct',
            geographyIds: ['hong_kong'],
            confidence: 'high',
            rationale: 'CDARS is used by the Hong Kong Hospital Authority.',
        },
    }),
    source({
        id: 'australian_pbs',
        label: 'Pharmaceutical Benefits Scheme',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['Pharmaceutical Benefits Scheme'],
        caseSensitiveAliases: ['PBS claims'],
        inference: {
            mode: 'direct',
            geographyIds: ['australia'],
            confidence: 'high',
            rationale: 'PBS is an Australian medicines reimbursement data source.',
        },
    }),
    source({
        id: 'australian_mbs',
        label: 'Medicare Benefits Schedule',
        scope: 'global',
        sourceType: 'claimsDatabase',
        aliases: ['Medicare Benefits Schedule'],
        caseSensitiveAliases: ['MBS claims'],
        inference: {
            mode: 'direct',
            geographyIds: ['australia'],
            confidence: 'high',
            rationale: 'MBS is an Australian health-services claims data source.',
        },
    }),

    // =========================================================
    // Middle East and Latin America
    // =========================================================
    source({
        id: 'clalit',
        label: 'Clalit Health Services',
        scope: 'global',
        sourceType: 'healthSystem',
        aliases: ['Clalit Health Services', 'Clalit healthcare database'],
        caseSensitiveAliases: ['Clalit'],
        inference: {
            mode: 'direct',
            geographyIds: ['israel'],
            confidence: 'high',
            rationale: 'Clalit is an Israeli health system.',
        },
    }),
    source({
        id: 'datasus',
        label: 'DATASUS',
        scope: 'global',
        sourceType: 'administrativeDatabase',
        aliases: ['Brazilian Unified Health System database', 'Departamento de Informática do Sistema Único de Saúde'],
        caseSensitiveAliases: ['DATASUS'],
        inference: {
            mode: 'direct',
            geographyIds: ['brazil'],
            confidence: 'high',
            rationale: 'DATASUS is a Brazilian public health data source.',
        },
    }),
    source({
        id: 'mexico_imss',
        label: 'Mexican Social Security Institute',
        scope: 'global',
        sourceType: 'healthSystem',
        aliases: ['Mexican Social Security Institute', 'Instituto Mexicano del Seguro Social'],
        caseSensitiveAliases: ['IMSS'],
        inference: {
            mode: 'direct',
            geographyIds: ['mexico'],
            confidence: 'high',
            rationale: 'IMSS is a Mexican national social-security health system.',
        },
    }),
]

/**
 * Disease- and therapy-area-specific sources.
 *
 * These should be loaded in addition to the global sources when the active
 * roadmap indication or search indication matches the configured indication.
 */
export const THERAPY_AREA_PUBMED_GEOGRAPHY_SOURCES: GeographySourceConfig[] = [
    // =========================================================
    // Rheumatoid arthritis and rheumatology
    // =========================================================
    source({
        id: 'corevitas_ra_registry',
        label: 'CorEvitas Rheumatoid Arthritis Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: [
            'CorEvitas Rheumatoid Arthritis Registry',
            'CorEvitas RA Registry',
            'Corrona Rheumatoid Arthritis Registry',
            'Corrona RA Registry',
        ],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'The CorEvitas/Corrona RA registry is a United States registry.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),
    source({
        id: 'bsrbr_ra',
        label: 'British Society for Rheumatology Biologics Register for RA',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: [
            'British Society for Rheumatology Biologics Register for Rheumatoid Arthritis',
            'British Society for Rheumatology Biologics Register for RA',
        ],
        caseSensitiveAliases: ['BSRBR-RA', 'BSRBR RA'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_kingdom'],
            confidence: 'high',
            rationale: 'BSRBR-RA is a United Kingdom rheumatoid arthritis register.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),
    source({
        id: 'rabbit_ra',
        label: 'RABBIT',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: [
            'German biologics register RABBIT',
            'Rheumatoid Arthritis Observation of Biologic Therapy',
            'Rheumatoide Arthritis Beobachtung der Biologika-Therapie',
        ],
        caseSensitiveAliases: ['RABBIT'],
        inference: {
            mode: 'direct',
            geographyIds: ['germany'],
            confidence: 'high',
            rationale: 'RABBIT is a German rheumatoid arthritis register.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),
    source({
        id: 'danbio',
        label: 'DANBIO',
        scope: 'therapyArea',
        sourceType: 'clinicalRegistry',
        aliases: ['Danish DANBIO registry', 'DANBIO registry'],
        caseSensitiveAliases: ['DANBIO'],
        inference: {
            mode: 'direct',
            geographyIds: ['denmark'],
            confidence: 'high',
            rationale: 'DANBIO is a nationwide Danish rheumatology registry.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
        notes: 'Also includes other inflammatory rheumatic diseases.',
    }),
    source({
        id: 'swedish_rheumatology_quality_register',
        label: 'Swedish Rheumatology Quality Register',
        scope: 'therapyArea',
        sourceType: 'clinicalRegistry',
        aliases: ['Swedish Rheumatology Quality Register', 'Swedish Rheumatology Register', 'ARTIS register'],
        caseSensitiveAliases: ['SRQ', 'ARTIS'],
        inference: {
            mode: 'direct',
            geographyIds: ['sweden'],
            confidence: 'high',
            rationale: 'SRQ/ARTIS is a Swedish rheumatology data source.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),
    source({
        id: 'nor_dmard',
        label: 'NOR-DMARD',
        scope: 'therapyArea',
        sourceType: 'clinicalRegistry',
        aliases: ['Norwegian DMARD register', 'Norwegian DMARD registry'],
        caseSensitiveAliases: ['NOR-DMARD'],
        inference: {
            mode: 'direct',
            geographyIds: ['norway'],
            confidence: 'high',
            rationale: 'NOR-DMARD is a Norwegian rheumatology register.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),
    source({
        id: 'biobadaser',
        label: 'BIOBADASER',
        scope: 'therapyArea',
        sourceType: 'clinicalRegistry',
        aliases: [
            'Spanish Registry for Adverse Events of Biological Therapy in Rheumatic Diseases',
            'Spanish biologics registry BIOBADASER',
        ],
        caseSensitiveAliases: ['BIOBADASER'],
        inference: {
            mode: 'direct',
            geographyIds: ['spain'],
            confidence: 'high',
            rationale: 'BIOBADASER is a Spanish rheumatology biologics registry.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),
    source({
        id: 'gisea',
        label: 'GISEA',
        scope: 'therapyArea',
        sourceType: 'clinicalRegistry',
        aliases: ['Italian Group for the Study of Early Arthritis'],
        caseSensitiveAliases: ['GISEA'],
        inference: {
            mode: 'direct',
            geographyIds: ['italy'],
            confidence: 'high',
            rationale: 'GISEA is an Italian rheumatology registry/network.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),
    source({
        id: 'iorra',
        label: 'IORRA',
        scope: 'therapyArea',
        sourceType: 'cohort',
        aliases: ['Institute of Rheumatology Rheumatoid Arthritis cohort'],
        caseSensitiveAliases: ['IORRA'],
        inference: {
            mode: 'direct',
            geographyIds: ['japan'],
            confidence: 'high',
            rationale: 'IORRA is a Japanese rheumatoid arthritis cohort.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),
    source({
        id: 'ninja',
        label: 'NinJa',
        scope: 'therapyArea',
        sourceType: 'clinicalRegistry',
        aliases: ['National Database of Rheumatic Diseases by iR-net in Japan'],
        caseSensitiveAliases: ['NinJa'],
        inference: {
            mode: 'direct',
            geographyIds: ['japan'],
            confidence: 'high',
            rationale: 'NinJa is a Japanese rheumatology database.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),
    source({
        id: 'meteor_ra',
        label: 'METEOR',
        scope: 'therapyArea',
        sourceType: 'researchNetwork',
        aliases: [
            'Measurement of Efficacy of Treatment in the Era of Outcome in Rheumatology',
            'METEOR rheumatoid arthritis database',
        ],
        caseSensitiveAliases: ['METEOR'],
        inference: {
            mode: 'none',
            geographyIds: [],
            confidence: 'low',
            rationale: 'METEOR is multinational and does not establish a single study country.',
        },
        therapyAreaIds: ['rheumatology'],
        indicationIds: ['rheumatoid_arthritis'],
    }),

    // =========================================================
    // Obesity and weight management
    // =========================================================
    source({
        id: 'national_weight_control_registry',
        label: 'National Weight Control Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['National Weight Control Registry'],
        caseSensitiveAliases: ['NWCR'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'NWCR is a United States weight-loss maintenance registry.',
        },
        therapyAreaIds: ['metabolic'],
        indicationIds: ['obesity', 'weight_management'],
    }),
    source({
        id: 'international_weight_control_registry',
        label: 'International Weight Control Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['International Weight Control Registry'],
        caseSensitiveAliases: ['IWCR'],
        inference: {
            mode: 'none',
            geographyIds: [],
            confidence: 'low',
            rationale: 'IWCR is international and does not establish a single study geography.',
        },
        therapyAreaIds: ['metabolic'],
        indicationIds: ['obesity', 'weight_management'],
    }),
    source({
        id: 'soreg',
        label: 'Scandinavian Obesity Surgery Registry',
        scope: 'therapyArea',
        sourceType: 'qualityRegistry',
        aliases: ['Scandinavian Obesity Surgery Registry', 'Scandinavian Obesity Surgery Register'],
        caseSensitiveAliases: ['SOReg', 'SOREG'],
        inference: {
            mode: 'direct',
            geographyIds: ['sweden'],
            confidence: 'high',
            rationale: 'Most SOReg publications refer to the nationwide Swedish registry.',
        },
        therapyAreaIds: ['metabolic'],
        indicationIds: ['obesity', 'weight_management'],
        notes: 'Norwegian participation exists; use contextual article evidence if SOReg-N is named.',
    }),
    source({
        id: 'uk_nbsr',
        label: 'UK National Bariatric Surgery Registry',
        scope: 'therapyArea',
        sourceType: 'qualityRegistry',
        aliases: ['National Bariatric Surgery Registry', 'UK National Bariatric Surgery Registry'],
        caseSensitiveAliases: ['NBSR'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_kingdom'],
            confidence: 'high',
            rationale: 'NBSR is the United Kingdom bariatric surgery registry.',
        },
        therapyAreaIds: ['metabolic'],
        indicationIds: ['obesity', 'weight_management'],
    }),
    source({
        id: 'swedish_obese_subjects',
        label: 'Swedish Obese Subjects Study',
        scope: 'therapyArea',
        sourceType: 'cohort',
        aliases: ['Swedish Obese Subjects Study', 'Swedish Obese Subjects cohort'],
        caseSensitiveAliases: ['SOS study'],
        inference: {
            mode: 'direct',
            geographyIds: ['sweden'],
            confidence: 'high',
            rationale: 'The Swedish Obese Subjects study is a Swedish cohort.',
        },
        therapyAreaIds: ['metabolic'],
        indicationIds: ['obesity', 'weight_management'],
    }),
    source({
        id: 'mbsaqip',
        label: 'Metabolic and Bariatric Surgery Accreditation and Quality Improvement Program',
        scope: 'therapyArea',
        sourceType: 'qualityRegistry',
        aliases: ['Metabolic and Bariatric Surgery Accreditation and Quality Improvement Program'],
        caseSensitiveAliases: ['MBSAQIP'],
        inference: {
            mode: 'contextual',
            geographyIds: ['united_states', 'canada'],
            confidence: 'medium',
            rationale: 'MBSAQIP is North American; the article may analyze one or both countries.',
        },
        therapyAreaIds: ['metabolic'],
        indicationIds: ['obesity', 'weight_management'],
    }),

    // =========================================================
    // Atopic dermatitis
    // =========================================================
    source({
        id: 'treatgermany',
        label: 'TREATgermany',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['German Atopic Dermatitis Registry', 'German atopic eczema registry', 'TREATgermany registry'],
        caseSensitiveAliases: ['TREATgermany'],
        inference: {
            mode: 'direct',
            geographyIds: ['germany'],
            confidence: 'high',
            rationale: 'TREATgermany is the German atopic dermatitis registry.',
        },
        therapyAreaIds: ['dermatology'],
        indicationIds: ['atopic_dermatitis'],
    }),
    source({
        id: 'a_star_atopic_eczema',
        label: 'UK-Irish Atopic Eczema Systemic Therapy Register',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: [
            'UK-Irish Atopic Eczema Systemic Therapy Register',
            'UK/Irish Atopic Eczema Systemic Therapy Register',
            'Atopic Eczema Systemic Therapy Register',
        ],
        caseSensitiveAliases: ['A-STAR'],
        inference: {
            mode: 'contextual',
            geographyIds: ['united_kingdom', 'ireland'],
            confidence: 'medium',
            rationale: 'A-STAR includes UK and Irish sites; individual analyses may use a subset.',
        },
        therapyAreaIds: ['dermatology'],
        indicationIds: ['atopic_dermatitis'],
    }),
    source({
        id: 'corevitas_ad_registry',
        label: 'CorEvitas Atopic Dermatitis Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['CorEvitas Atopic Dermatitis Registry', 'CorEvitas AD Registry'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'The core CorEvitas AD registry is a United States registry.',
        },
        therapyAreaIds: ['dermatology'],
        indicationIds: ['atopic_dermatitis'],
        notes: 'Treat separately from the International Adolescent AD Registry.',
    }),
    source({
        id: 'corevitas_international_adolescent_ad',
        label: 'CorEvitas International Adolescent Atopic Dermatitis Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: [
            'CorEvitas International Adolescent Atopic Dermatitis Registry',
            'CorEvitas International Adolescent AD Registry',
        ],
        inference: {
            mode: 'none',
            geographyIds: [],
            confidence: 'low',
            rationale: 'The registry is international and does not establish one study geography.',
        },
        therapyAreaIds: ['dermatology'],
        indicationIds: ['atopic_dermatitis'],
    }),

    // =========================================================
    // COPD
    // =========================================================
    source({
        id: 'copdgene',
        label: 'COPDGene',
        scope: 'therapyArea',
        sourceType: 'cohort',
        aliases: ['COPDGene Study', 'COPDGene cohort'],
        caseSensitiveAliases: ['COPDGene'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'COPDGene is a United States multicenter cohort.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['copd'],
    }),
    source({
        id: 'spiromics',
        label: 'SPIROMICS',
        scope: 'therapyArea',
        sourceType: 'cohort',
        aliases: ['Subpopulations and Intermediate Outcome Measures in COPD Study', 'SPIROMICS cohort'],
        caseSensitiveAliases: ['SPIROMICS'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'SPIROMICS is a United States multicenter COPD cohort.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['copd'],
    }),
    source({
        id: 'cosyconet',
        label: 'COSYCONET',
        scope: 'therapyArea',
        sourceType: 'cohort',
        aliases: [
            'German COPD and Systemic Consequences-Comorbidities Network',
            'COPD and Systemic Consequences-Comorbidities Network',
            'German COPD cohort COSYCONET',
        ],
        caseSensitiveAliases: ['COSYCONET'],
        inference: {
            mode: 'direct',
            geographyIds: ['germany'],
            confidence: 'high',
            rationale: 'COSYCONET is a German COPD cohort.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['copd'],
    }),
    source({
        id: 'cancold',
        label: 'CanCOLD',
        scope: 'therapyArea',
        sourceType: 'cohort',
        aliases: ['Canadian Cohort Obstructive Lung Disease', 'Canadian Cohort of Obstructive Lung Disease'],
        caseSensitiveAliases: ['CanCOLD'],
        inference: {
            mode: 'direct',
            geographyIds: ['canada'],
            confidence: 'high',
            rationale: 'CanCOLD is a Canada-wide COPD cohort.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['copd'],
    }),
    source({
        id: 'kocoss',
        label: 'Korean COPD Subgroup Study',
        scope: 'therapyArea',
        sourceType: 'cohort',
        aliases: ['Korean COPD Subgroup Study'],
        caseSensitiveAliases: ['KOCOSS'],
        inference: {
            mode: 'direct',
            geographyIds: ['south_korea'],
            confidence: 'high',
            rationale: 'KOCOSS is a South Korean COPD cohort.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['copd'],
    }),
    source({
        id: 'eclipse_copd',
        label: 'ECLIPSE',
        scope: 'therapyArea',
        sourceType: 'cohort',
        aliases: [
            'Evaluation of COPD Longitudinally to Identify Predictive Surrogate End-points',
            'ECLIPSE COPD cohort',
        ],
        caseSensitiveAliases: ['ECLIPSE'],
        inference: {
            mode: 'none',
            geographyIds: [],
            confidence: 'low',
            rationale: 'ECLIPSE is multinational and does not establish a single study country.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['copd'],
    }),

    // =========================================================
    // Asthma and severe asthma
    // =========================================================
    source({
        id: 'isar',
        label: 'International Severe Asthma Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['International Severe Asthma Registry'],
        caseSensitiveAliases: ['ISAR'],
        inference: {
            mode: 'none',
            geographyIds: [],
            confidence: 'low',
            rationale: 'ISAR is multinational and does not establish a single country.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['asthma', 'severe_asthma'],
    }),
    source({
        id: 'uk_severe_asthma_registry',
        label: 'UK Severe Asthma Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['UK Severe Asthma Registry', 'British Thoracic Society Severe Asthma Registry'],
        caseSensitiveAliases: ['UKSAR'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_kingdom'],
            confidence: 'high',
            rationale: 'UKSAR is a United Kingdom severe asthma registry.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['asthma', 'severe_asthma'],
    }),
    source({
        id: 'sani',
        label: 'Severe Asthma Network Italy',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['Severe Asthma Network Italy', 'Severe Asthma Network in Italy'],
        caseSensitiveAliases: ['SANI'],
        inference: {
            mode: 'direct',
            geographyIds: ['italy'],
            confidence: 'high',
            rationale: 'SANI is an Italian severe asthma registry.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['asthma', 'severe_asthma'],
    }),
    source({
        id: 'german_asthma_net',
        label: 'German Asthma Net',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['German Asthma Net', 'German Severe Asthma Registry'],
        caseSensitiveAliases: ['GAN'],
        inference: {
            mode: 'direct',
            geographyIds: ['germany'],
            confidence: 'high',
            rationale: 'German Asthma Net is a German severe asthma network/registry.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['asthma', 'severe_asthma'],
    }),
    source({
        id: 'belgian_severe_asthma_registry',
        label: 'Belgian Severe Asthma Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['Belgian Severe Asthma Registry'],
        caseSensitiveAliases: ['BSAR'],
        inference: {
            mode: 'direct',
            geographyIds: ['belgium'],
            confidence: 'high',
            rationale: 'BSAR is a Belgian severe asthma registry.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['asthma', 'severe_asthma'],
    }),
    source({
        id: 'swedish_national_airway_register',
        label: 'Swedish National Airway Register',
        scope: 'therapyArea',
        sourceType: 'clinicalRegistry',
        aliases: ['Swedish National Airway Register', 'Swedish National Airway Registry'],
        caseSensitiveAliases: ['SNAR'],
        inference: {
            mode: 'direct',
            geographyIds: ['sweden'],
            confidence: 'high',
            rationale: 'SNAR is a Swedish registry for asthma and COPD.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['asthma', 'severe_asthma', 'copd'],
    }),
    source({
        id: 'australasian_severe_asthma_registry',
        label: 'Australasian Severe Asthma Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['Australasian Severe Asthma Registry'],
        caseSensitiveAliases: ['ASAR'],
        inference: {
            mode: 'contextual',
            geographyIds: ['australia', 'new_zealand'],
            confidence: 'medium',
            rationale: 'ASAR spans Australia and New Zealand; analyses may use one or both.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['asthma', 'severe_asthma'],
    }),
    source({
        id: 'chronicle_asthma',
        label: 'CHRONICLE',
        scope: 'therapyArea',
        sourceType: 'cohort',
        aliases: ['CHRONICLE severe asthma study', 'CHRONICLE study'],
        caseSensitiveAliases: ['CHRONICLE'],
        inference: {
            mode: 'direct',
            geographyIds: ['united_states'],
            confidence: 'high',
            rationale: 'CHRONICLE is a United States severe asthma observational study.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['asthma', 'severe_asthma'],
    }),
    source({
        id: 'kosar',
        label: 'Korean Severe Asthma Registry',
        scope: 'therapyArea',
        sourceType: 'diseaseRegistry',
        aliases: ['Korean Severe Asthma Registry'],
        caseSensitiveAliases: ['KoSAR', 'KOSAR'],
        inference: {
            mode: 'direct',
            geographyIds: ['south_korea'],
            confidence: 'high',
            rationale: 'KoSAR is a South Korean severe asthma registry.',
        },
        therapyAreaIds: ['respiratory'],
        indicationIds: ['asthma', 'severe_asthma'],
    }),
]

export const PUBMED_GEOGRAPHY_BY_ID = new Map(PUBMED_GEOGRAPHY_ENTITIES.map((entity) => [entity.id, entity]))

export const PUBMED_GEOGRAPHY_REGION_BY_ID = new Map(PUBMED_GEOGRAPHY_REGIONS.map((region) => [region.id, region]))

export const PUBMED_GEOGRAPHY_SOURCE_BY_ID = new Map(
    [...CORE_PUBMED_GEOGRAPHY_SOURCES, ...THERAPY_AREA_PUBMED_GEOGRAPHY_SOURCES].map((item) => [item.id, item])
)

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const literalPattern = (value: string, caseInsensitive: boolean): RegExp =>
    new RegExp(`\\b${escapeRegExp(value)}\\b`, caseInsensitive ? 'i' : undefined)

/**
 * Returns literal and custom patterns for a geography entity.
 *
 * `contextualDemonyms` are intentionally excluded. Use
 * `getContextualDemonymPatterns()` for those.
 */
export const getExplicitGeographyPatterns = (entity: GeographyEntityConfig): RegExp[] => [
    ...entity.aliases.map((alias) => literalPattern(alias, true)),
    ...(entity.caseSensitiveAliases ?? []).map((alias) => literalPattern(alias, false)),
    ...(entity.patterns ?? []),
]

/**
 * Builds guarded demonym patterns.
 *
 * Example output:
 *   /\bGerman\s+(?:patients?|population|cohort|...)\b/i
 *
 * This reduces false positives such as "American College of Rheumatology".
 */
export const getContextualDemonymPatterns = (entity: GeographyEntityConfig): RegExp[] => {
    const contextGroup = PUBMED_GEOGRAPHY_CONTEXT_TERMS.join('|')

    return (entity.contextualDemonyms ?? []).map(
        (demonym) => new RegExp(`\\b${escapeRegExp(demonym)}\\s+(?:${contextGroup})\\b`, 'i')
    )
}

export const getGeographySourcePatterns = (item: GeographySourceConfig): RegExp[] => [
    ...item.aliases.map((alias) => literalPattern(alias, true)),
    ...(item.caseSensitiveAliases ?? []).map((alias) => literalPattern(alias, false)),
    ...(item.patterns ?? []),
]

/**
 * Combines global sources with indication-relevant registries.
 *
 * This mirrors the pattern used for core and indication-specific keyword families.
 */
export const getActivePubMedGeographySources = (indicationId?: GeographyIndicationId): GeographySourceConfig[] => {
    if (!indicationId) {
        return CORE_PUBMED_GEOGRAPHY_SOURCES.filter((item) => item.enabled !== false)
    }

    return [
        ...CORE_PUBMED_GEOGRAPHY_SOURCES,
        ...THERAPY_AREA_PUBMED_GEOGRAPHY_SOURCES.filter((item) => item.indicationIds?.includes(indicationId)),
    ].filter((item) => item.enabled !== false)
}

/**
 * Returns parent geography IDs without replacing the original geography.
 *
 * Example:
 *   Scotland -> ['united_kingdom']
 *   Puerto Rico -> ['united_states']
 */
export const getParentGeographyIds = (geographyId: string): string[] => {
    const parents: string[] = []
    const seen = new Set<string>()

    let current = PUBMED_GEOGRAPHY_BY_ID.get(geographyId)

    while (current?.parentGeographyId) {
        const parentId = current.parentGeographyId

        if (seen.has(parentId)) {
            break
        }

        seen.add(parentId)
        parents.push(parentId)
        current = PUBMED_GEOGRAPHY_BY_ID.get(parentId)
    }

    return parents
}

/**
 * Returns all region rollups, including parent regions.
 *
 * Example:
 *   Germany -> ['europe', 'western_europe']
 *   Sweden -> ['europe', 'northern_europe']
 */
export const getRegionRollupIds = (geographyId: string): string[] => {
    const entity = PUBMED_GEOGRAPHY_BY_ID.get(geographyId)

    if (!entity) {
        return []
    }

    const regionIds = new Set<string>()

    const addRegionAndParents = (regionId: string): void => {
        if (regionIds.has(regionId)) {
            return
        }

        regionIds.add(regionId)

        const region = PUBMED_GEOGRAPHY_REGION_BY_ID.get(regionId)

        if (region?.parentRegionId) {
            addRegionAndParents(region.parentRegionId)
        }
    }

    entity.regionIds.forEach(addRegionAndParents)

    return [...regionIds]
}

/**
 * Returns filter rollups for a set of specifically detected geographies.
 */
export const buildGeographyRollups = (
    geographyIds: string[]
): {
    geographyRollupIds: string[]
    regionRollupIds: string[]
} => {
    const geographyRollupIds = new Set<string>()
    const regionRollupIds = new Set<string>()

    geographyIds.forEach((geographyId) => {
        getParentGeographyIds(geographyId).forEach((id) => geographyRollupIds.add(id))
        getRegionRollupIds(geographyId).forEach((id) => regionRollupIds.add(id))
    })

    return {
        geographyRollupIds: [...geographyRollupIds],
        regionRollupIds: [...regionRollupIds],
    }
}

/**
 * Lightweight configuration validation for development and tests.
 */
export const validatePubMedGeographyConfig = (): string[] => {
    const errors: string[] = []

    const duplicateValues = (values: string[], label: string): void => {
        const seen = new Set<string>()

        values.forEach((value) => {
            if (seen.has(value)) {
                errors.push(`Duplicate ${label}: ${value}`)
            }

            seen.add(value)
        })
    }

    duplicateValues(
        PUBMED_GEOGRAPHY_ENTITIES.map((item) => item.id),
        'geography ID'
    )
    duplicateValues(
        PUBMED_GEOGRAPHY_REGIONS.map((item) => item.id),
        'region ID'
    )
    duplicateValues(
        [...CORE_PUBMED_GEOGRAPHY_SOURCES, ...THERAPY_AREA_PUBMED_GEOGRAPHY_SOURCES].map((item) => item.id),
        'source ID'
    )

    PUBMED_GEOGRAPHY_ENTITIES.forEach((entity) => {
        if (entity.parentGeographyId && !PUBMED_GEOGRAPHY_BY_ID.has(entity.parentGeographyId)) {
            errors.push(`Unknown parent geography "${entity.parentGeographyId}" for "${entity.id}"`)
        }

        entity.regionIds.forEach((regionId) => {
            if (!PUBMED_GEOGRAPHY_REGION_BY_ID.has(regionId)) {
                errors.push(`Unknown region "${regionId}" for geography "${entity.id}"`)
            }
        })
    })

    const allSources = [...CORE_PUBMED_GEOGRAPHY_SOURCES, ...THERAPY_AREA_PUBMED_GEOGRAPHY_SOURCES]

    allSources.forEach((item) => {
        item.inference.geographyIds.forEach((geographyId) => {
            if (!PUBMED_GEOGRAPHY_BY_ID.has(geographyId)) {
                errors.push(`Unknown inferred geography "${geographyId}" for source "${item.id}"`)
            }
        })

        if (item.inference.mode === 'direct' && item.inference.geographyIds.length === 0) {
            errors.push(`Direct source "${item.id}" must contain at least one geography ID`)
        }

        if (item.inference.mode === 'none' && item.inference.geographyIds.length > 0) {
            errors.push(`Source "${item.id}" uses inference mode "none" but contains geography IDs`)
        }
    })

    return errors
}
